import { useCallback, useEffect, useRef } from "react";

export interface AEvent<TEventName extends string = string> {
  name: TEventName;
  properties?: Record<string, unknown>;
  encryptedProperties?: string[];
}

export type EventName<TEvent extends AEvent> = TEvent["name"];
export type EventData<TEvent extends AEvent> = Omit<TEvent, "name">;

export type EventTransformerHooks<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> = {
  [TK in EventName<TSourceEvent>]?: UseTransformEvent<
    Extract<TSourceEvent, AEvent<TK>>,
    TTargetEvent
  >[];
};

export type UseTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> =
  | UseInstantTransformEvent<TSourceEvent, TTargetEvent>
  | UseDeferTransformEvent<TSourceEvent, TTargetEvent>;

export type UseDeferTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> = (
  name: EventName<TSourceEvent>
) => DeferTransformEvent<
  Extract<TSourceEvent, AEvent<EventName<TSourceEvent>>>,
  TTargetEvent
>;

export type UseInstantTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> = (
  name: EventName<TSourceEvent>
) => InstantTransformEvent<
  Extract<TSourceEvent, AEvent<EventName<TSourceEvent>>>,
  TTargetEvent
>;

export type TransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> =
  | InstantTransformEvent<TSourceEvent, TTargetEvent>
  | DeferTransformEvent<TSourceEvent, TTargetEvent>;

export type InstantTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> = (event: EventData<TSourceEvent>) => TTargetEvent | undefined;

export function createUseEmitRawEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent,
  THooks extends EventTransformerHooks<TSourceEvent, TTargetEvent>,
  TExtractedSourceEvent extends ExtractSourceEvent<THooks>,
  TExtractedTargetEvent extends ExtractTargetEvent<THooks>
>(config: {
  transformerHooks: THooks;
  usePublishEvent: UsePublishEvent<TExtractedTargetEvent>;
}): UseEmitRawEvent<TExtractedSourceEvent> {
  const { transformerHooks, usePublishEvent } = config;

  return function useEmitEvent(
    eventName: EventName<TExtractedSourceEvent>
  ): EmitRawEvent<TExtractedSourceEvent> {
    type TExtractedSourceEventData = EventData<TExtractedSourceEvent>;
    const publishEvent = usePublishEvent();

    const eventDataToPublishQueueRef = useRef<TExtractedSourceEventData[]>([]);

    const useTransformEventHooks = transformerHooks[eventName];

    const transformEventConfs = useLyftTransformers<
      TExtractedSourceEvent,
      TExtractedTargetEvent
    >(eventName, useTransformEventHooks ?? []);

    const allDefferTransformEvent = transformEventConfs.filter(
      isDeferTransformEvent
    );

    const allDefferCanTransform = allDefferTransformEvent.map(
      (deferTransformEvent) => deferTransformEvent.useCanTransform()
    );

    const publishReadyQueuedEvents = useCallback(() => {
      for (let i = 0; i < allDefferTransformEvent.length; i++) {
        const canTransform = allDefferCanTransform[i];
        const transformEvent = allDefferTransformEvent[i].transformEvent;

        if (!canTransform) {
          continue;
        }

        if (eventDataToPublishQueueRef.current.length > 0) {
          eventDataToPublishQueueRef.current.forEach((eventData) => {
            const transformedEvent = transformEvent(eventData) as
              | TExtractedTargetEvent
              | undefined;

            if (transformedEvent !== undefined) {
              publishEvent(transformedEvent);
            }
          });

          eventDataToPublishQueueRef.current = [];
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...allDefferTransformEvent, ...allDefferCanTransform, publishEvent]);

    useEffect(() => {
      publishReadyQueuedEvents();
    }, [publishEvent, publishReadyQueuedEvents]);

    const allInstantTransformEvent = transformEventConfs.filter(
      isInstantTransformEvent
    );

    return useCallback(
      (eventData) => {
        eventDataToPublishQueueRef.current.push(eventData);

        allInstantTransformEvent
          .flatMap((transformEvent) => {
            return transformEvent(eventData) ?? [];
          })
          .map((transformedEvent) => publishEvent(transformedEvent));

        publishReadyQueuedEvents();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        publishEvent,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ...allDefferTransformEvent,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ...allDefferCanTransform,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ...allInstantTransformEvent,
      ]
    );
  };
}

export type UseEmitRawEvent<TSourceEvent extends AEvent = AEvent> = <
  TName extends EventName<TSourceEvent>
>(
  eventName: TName
) => EmitRawEvent<Extract<TSourceEvent, AEvent<TName>>>;

export type SmartEmitRawEvent<TSourceEvent extends AEvent> = IsAllOptional<
  EventData<TSourceEvent>
> extends true
  ? EmptyEmitRawEvent<TSourceEvent>
  : EmitRawEvent<TSourceEvent>;

export type EmitRawEvent<TSourceEvent extends AEvent> = (
  event: EventData<TSourceEvent>
) => void;

export type EmptyEmitRawEvent<TSourceEvent extends AEvent> = (
  event?: EventData<TSourceEvent>
) => void;

export type UsePublishEvent<TTargetEvent extends AEvent> =
  () => PublishEvent<TTargetEvent>;

export type PublishEvent<TTargetEvent extends AEvent> = (
  event: TTargetEvent
) => void;

export class DeferTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
> {
  constructor(
    public readonly useCanTransform: () => boolean,
    public readonly transformEvent: InstantTransformEvent<
      TSourceEvent,
      TTargetEvent
    >
  ) {}
}

function useLyftTransformers<
  TExtractedSourceEvent extends AEvent,
  TExtractedTargetEvent extends AEvent
>(
  eventName: EventName<TExtractedSourceEvent>,
  useTransformEventHooks: Array<
    UseTransformEvent<AEvent<EventName<TExtractedSourceEvent>>, AEvent>
  >
): Array<TransformEvent<TExtractedSourceEvent, TExtractedTargetEvent>> {
  return useTransformEventHooks.map(function useRoot(useTransformEvent) {
    return useTransformEvent(
      eventName
    ) as TransformEvent<TExtractedSourceEvent, TExtractedTargetEvent>;
  });
}

function isInstantTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
>(
  transformEvent: TransformEvent<TSourceEvent, TTargetEvent>
): transformEvent is InstantTransformEvent<TSourceEvent, TTargetEvent> {
  return !isDeferTransformEvent(transformEvent);
}

function isDeferTransformEvent<
  TSourceEvent extends AEvent,
  TTargetEvent extends AEvent
>(
  transformEvent: TransformEvent<TSourceEvent, TTargetEvent>
): transformEvent is DeferTransformEvent<TSourceEvent, TTargetEvent> {
  return transformEvent instanceof DeferTransformEvent;
}

type IsAllOptional<T> = Partial<T> extends T ? true : false;

// the `any` are nececery here to allow proper type infer
/* eslint-disable @typescript-eslint/no-explicit-any */
export type ExtractSourceEvent<T> = T extends EventTransformerHooks<
  infer R,
  any
>
  ? R
  : never;

export type ExtractTargetEvent<T> = T extends EventTransformerHooks<
  any,
  infer E
>
  ? E
  : never;
/* eslint-enable @typescript-eslint/no-explicit-any */
